import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IAccreditationApplicationForm from 'interfaces/IAccreditationApplicationForm';
import { AccreditationType, LockOwner, MemberProfile } from 'graphql/__generated__/graphql';
import IClinicalContact from 'interfaces/IClinicalContact';
import ISupervisor from 'interfaces/ISupervisor';
import IApplicationRequirements, {
    IAccreditationApplicationRequirements,
} from 'interfaces/IApplicationRequirements';
import ISimpleNameValuePair from 'interfaces/ISimpleNameValuePair';
import IMember from 'interfaces/IMember';
import _ from 'lodash';
import { IAccreditationApplication } from 'interfaces/IAccreditationApplication';
import { useApolloClient, useQuery } from '@apollo/client';
import { GET_PROFILE } from 'graphql/profile/queries';
import useApplicationFormStepsConfig from './useApplicationFormStepsConfig';
import { useAccreditationApplication } from '../../../../hooks/useAccreditationApplication';
import { OperationResult } from '../../../../interfaces/OperationResult';
import IActivity from '../../../../interfaces/IActivity';
import { useAccreditationTypes } from '../../../../hooks/useAccreditationTypes';

const defaultMemberProfile = {
    id: '',
    firstName: '',
    lastName: '',
    address: {
        addressLine1: '',
        addressLine2: '',
        city: '',
        postcode: '',
        country: '',
        regionId: '',
    },
    phone: '',
    email: '',
    qualifications: '',
    profession: '',
};

// Moved outside to prevent recreation
function applicationToForm(
    accreditationApplication: IAccreditationApplication
): IAccreditationApplicationForm {
    if (!accreditationApplication) return {} as IAccreditationApplicationForm;

    const { accreditationApplicationId, accreditationType, applicationStatus, jsonData } =
        accreditationApplication;
    const appData = JSON.parse(jsonData || '{}');
    const { experiences, clinicalContacts, supervisors, documents, activities, activityDetails } =
        appData;

    return {
        applicationId: accreditationApplicationId,
        accreditationTypeId: accreditationType?.accreditationTypeId,
        applicationStatus,
        experiences,
        clinicalContacts,
        supervisors,
        documents,
        activities,
        activityDetails,
    };
}

// Moved outside to prevent recreation
function determineUniqueSupervisors(
    currentSupervisors: ISupervisor[] = [],
    clinicalContacts: IClinicalContact[] = []
): ISupervisor[] {
    const uniqueClinicalContactSupervisors: IMember[] = _.uniqBy(
        clinicalContacts.map((cc) => cc.supervisor),
        (x) => x.id
    );

    const uniqueSupervisors = uniqueClinicalContactSupervisors
        .map((ccs) => currentSupervisors.find((s) => s.id === ccs.id) || (ccs as ISupervisor))
        .filter((ccs) => ccs.id);

    return uniqueSupervisors.map((supervisor) => {
        const associatedClients = clinicalContacts.filter(
            (cc) => cc.supervisor.id === supervisor.id
        );

        const sessionCount = associatedClients
            .map((cc) => Number(cc.numberOfSessions || 0))
            .reduce((total, current) => total + current, 0);

        return {
            ...supervisor,
            numberOfClients: associatedClients.length,
            numberOfSessions: sessionCount,
        };
    });
}

function checkLocalStorage(identifier?: string) {
    if (!identifier) return;
    const backup = localStorage.getItem(identifier);

    if (!backup) return null;
    try {
        return JSON.parse(backup);
    } catch (e) {
        // return null;
    }
}

function generateApplication(accreditationType: AccreditationType, renewal: boolean) {
    return {
        accreditationType,
        lockedBy: LockOwner.Applicant,
        jsonData: '{}',
        hasMitigation: false,
        isRenewal: renewal,
    } as IAccreditationApplication;
}

export const useAccreditationApplicationFormComponent = (
    accreditationApplicationId?: string,
    accreditationTypeId?: string,
    renewal = false
) => {
    const locallyStoredApplication = checkLocalStorage(accreditationApplicationId);

    const {
        models: { loading: appLoading, updating: appUpdating, error: appError },
        operations: { fetchApplication, upsertApplication, submitApplication },
    } = useAccreditationApplication();

    const { accreditationTypes, loading: accreditationTypesLoading } = useAccreditationTypes();

    const client = useApolloClient();
    const {
        data: profileData,
        loading: profileLoading,
        error: profileError,
    } = useQuery(GET_PROFILE, { client });

    const [loading, setLoading] = useState<boolean>(true);
    const [updating, setUpdating] = useState<boolean>(false);
    const [memberProfile, setMemberProfile] = useState<MemberProfile>(defaultMemberProfile);
    const [operationResult, setOperationResult] = useState<OperationResult>();
    const [application, setApplication] = useState<IAccreditationApplication>();

    useEffect(() => {
        setLoading(appLoading || profileLoading || accreditationTypesLoading);
        setUpdating(appUpdating);
    }, [appLoading, appUpdating, profileLoading, accreditationTypesLoading]);

    useEffect(() => {
        if (locallyStoredApplication) {
            setApplication(locallyStoredApplication);
            setLoading(false);
        } else {
            if (accreditationApplicationId) {
                fetchApplication(accreditationApplicationId).then((fetchedApplication) => {
                    if (fetchedApplication) {
                        setApplication(fetchedApplication);
                    }
                });
            } else {
                if (accreditationTypeId) {
                    const accreditationType = accreditationTypes.find(
                        (type) => type.accreditationTypeId === accreditationTypeId
                    );

                    setApplication(generateApplication(accreditationType!, renewal));
                }
            }
        }
    }, []);

    // Memoize form data based on application
    const initialFormData = useMemo(
        () =>
            application ? applicationToForm(application) : ({} as IAccreditationApplicationForm),
        [application]
    );

    const [formData, setFormData] = useState<IAccreditationApplicationForm>(initialFormData);

    // Update form data when application changes
    useEffect(() => {
        if (application) {
            setFormData(applicationToForm(application));
        }
    }, [application]);

    const {
        stepsConfig,
        formValidator,
        handleCanSkipToStep,
        handleNextStep,
        currentStep,
        validated,
        ...steps
    } = useApplicationFormStepsConfig(formData, application);

    // Memoize requirements calculation
    const requirements = useMemo(() => {
        if (!application?.accreditationType?.requirementsJson) {
            return {} as IAccreditationApplicationRequirements;
        }

        const requirementsBase: IApplicationRequirements = JSON.parse(
            application.accreditationType.requirementsJson
        );

        return application.isRenewal
            ? requirementsBase.reaccreditation
            : requirementsBase.accreditation;
    }, [application?.accreditationType?.requirementsJson, application?.isRenewal]);

    useEffect(() => {
        if (!profileError && !profileLoading && profileData?.profile) {
            setMemberProfile(profileData.profile);
        }
    }, [profileData, profileError, profileLoading]);

    // Memoize input change handlers
    const handleInputChangeEvent = useCallback(
        (
            event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
        ): void => {
            const { name, value } = event.target;
            handleInputChange(name, value);
        },
        [] // No dependencies as it calls another memoized function
    );

    const handleSaveProgress = async (formData: IAccreditationApplicationForm) => {
        if (updating || !application) return;

        setUpdating(true);

        const updatedApplication: IAccreditationApplication = {
            ...application,
            jsonData: JSON.stringify(formData),
        };

        return new Promise<string | void>((resolve, reject) => {
            upsertApplication(updatedApplication)
                .then((application: IAccreditationApplication) => {
                    application.accreditationApplicationId &&
                        localStorage.removeItem(application.accreditationApplicationId);

                    setApplication((prevState) => ({
                        ...prevState,
                        ...application,
                    }));
                    resolve(application.accreditationApplicationId);
                })
                .catch((error) => {
                    console.error('An error occurred saving the application', error);
                    updatedApplication.accreditationApplicationId &&
                        localStorage.setItem(
                            updatedApplication.accreditationApplicationId,
                            JSON.stringify(updatedApplication)
                        );

                    reject(error);
                })
                .finally(() => setUpdating(false));
        });
    };

    const handleInputChange = useCallback(
        (
            name: string,
            value:
                | string
                | string[]
                | boolean
                | IClinicalContact[]
                | ISupervisor[]
                | ISimpleNameValuePair[]
                | IActivity[]
        ): void => {
            setFormData((prevData) => {
                const updatedData = {
                    ...prevData,
                    [name]: value,
                };

                if (name === 'clinicalContacts') {
                    return {
                        ...updatedData,
                        supervisors: determineUniqueSupervisors(
                            updatedData.supervisors,
                            updatedData.clinicalContacts
                        ),
                    };
                }

                return updatedData;
            });
        },
        [] // No external dependencies needed
    );

    const notifyApplicationSaved = useCallback(
        () => setOperationResult({ success: true, message: 'Application saved' }),
        []
    );

    const handleSetStep = useCallback(
        (step: number) => {
            handleSaveProgress(formData)
                .then((applicationId) => {
                    if (!formData.applicationId && applicationId) {
                        setFormData((prevState) => ({
                            ...prevState,
                            applicationId,
                        }));
                    }
                    notifyApplicationSaved();
                    const errors = steps.handleSetStep(step);
                    if (errors.length) {
                        setOperationResult({
                            success: false,
                            message: errors,
                        });
                    }
                })
                .catch(() => {
                    setOperationResult({
                        success: false,
                        message: 'An error occurred saving the application',
                    });
                });
        },
        [formData, handleSaveProgress, steps, notifyApplicationSaved]
    );

    const handleSubmitApplication = useCallback(async () => {
        return await submitApplication(application!);
    }, [application, submitApplication]);

    const handleFetchApplication = useCallback(async () => {
        return await fetchApplication(application?.accreditationApplicationId!);
    }, [application, fetchApplication]);

    // Memoize the returned object to maintain reference stability
    return useMemo(
        () => ({
            models: {
                formData,
                memberProfile,
                currentStep,
                updating,
                requirements,
                stepsConfig,
                application,
                loading,
                validated,
                operationResult,
                formValidator,
            },
            operations: {
                handleCanSkipToStep,
                handleInputChangeEvent,
                handleInputChange,
                handleNextStep,
                handleSaveProgress,
                handleSetStep,
                handleSubmitApplication,
                handleFetchApplication,
            },
        }),
        [
            formData,
            memberProfile,
            currentStep,
            updating,
            requirements,
            stepsConfig,
            application,
            loading,
            validated,
            operationResult,
            formValidator,
            handleCanSkipToStep,
            handleInputChangeEvent,
            handleInputChange,
            handleNextStep,
            handleSaveProgress,
            handleSetStep,
            handleSubmitApplication,
            handleFetchApplication,
        ]
    );
};
